import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    DatePicker,
    Select,
    Tag,
    Progress,
    Space,
    message,
    Row,
    Col
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    CalculatorOutlined,
    EyeOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;
const { RangePicker } = DatePicker;

const BlockPlanning = () => {
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchBlocks();
    }, []);

    const fetchBlocks = async () => {
        setLoading(true);
        try {
            const response = await api.get('/blocks');
            setBlocks(response.data);
        } catch (error) {
            message.error('Failed to fetch blocks');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (selectedBlock) {
                await api.put(`/blocks/${selectedBlock.id}`, values);
                message.success('Block updated successfully');
            } else {
                await api.post('/blocks', values);
                message.success('Block created successfully');
            }
            setModalVisible(false);
            fetchBlocks();
            form.resetFields();
            setSelectedBlock(null);
        } catch (error) {
            message.error('Operation failed');
        }
    };

    const calculateCosts = async (blockId) => {
        try {
            await api.post(`/blocks/${blockId}/calculate-costs`);
            message.success('Costs calculated successfully');
            fetchBlocks();
        } catch (error) {
            message.error('Failed to calculate costs');
        }
    };

    const columns = [
        {
            title: 'Block Code',
            dataIndex: 'block_code',
            key: 'block_code',
        },
        {
            title: 'Block Name',
            dataIndex: 'block_name',
            key: 'block_name',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    planned: 'blue',
                    in_progress: 'green',
                    completed: 'purple',
                    on_hold: 'orange'
                };
                return <Tag color={colors[status]}>{status.replace('_', ' ').toUpperCase()}</Tag>;
            },
        },
        {
            title: 'Progress',
            key: 'progress',
            render: (_, record) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>Soft: {record.soft_soil_moved}/{record.total_soft_soil} m³</div>
                    <Progress percent={record.soft_progress || 0} size="small" />
                    <div>Coal: {record.coal_moved}/{record.total_coal} tons</div>
                    <Progress percent={record.coal_progress || 0} size="small" />
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EyeOutlined />}
                        size="small"
                        onClick={() => {/* Navigate to details */}}
                    />
                    <Button
                        icon={<CalculatorOutlined />}
                        size="small"
                        onClick={() => calculateCosts(record.id)}
                    />
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                            setSelectedBlock(record);
                            form.setFieldsValue(record);
                            setModalVisible(true);
                        }}
                    />
                </Space>
            ),
        },
    ];

    return (
        <>
            <Card
                title="Mining Blocks"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setSelectedBlock(null);
                            form.resetFields();
                            setModalVisible(true);
                        }}
                    >
                        New Block
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={blocks}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={selectedBlock ? 'Edit Block' : 'Create New Block'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setSelectedBlock(null);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="block_code"
                                label="Block Code"
                                rules={[{ required: true, message: 'Please enter block code' }]}
                            >
                                <Input placeholder="e.g., S2B8" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="block_name"
                                label="Block Name"
                                rules={[{ required: true, message: 'Please enter block name' }]}
                            >
                                <Input placeholder="e.g., S2B8 Mining Block" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="total_soft_soil"
                                label="Soft Soil (m³)"
                                rules={[{ required: true, message: 'Please enter volume' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={100}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="total_hard_soil"
                                label="Hard Soil (m³)"
                                rules={[{ required: true, message: 'Please enter volume' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={100}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="total_coal"
                                label="Coal (tons)"
                                rules={[{ required: true, message: 'Please enter volume' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={100}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="planned_completion_date"
                        label="Planned Completion Date"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    {selectedBlock && (
                        <Form.Item
                            name="status"
                            label="Status"
                        >
                            <Select>
                                <Option value="planned">Planned</Option>
                                <Option value="in_progress">In Progress</Option>
                                <Option value="completed">Completed</Option>
                                <Option value="on_hold">On Hold</Option>
                            </Select>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default BlockPlanning;